import { useCallback, useEffect, useState } from 'react'
import {
  HOOD_MAP,
  MexicoCityHoods,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'

const STORAGE_KEY = 'spotme:last_search'
const DISMISS_KEY = 'spotme:last_search_dismissed'
// Storage rental has a longer consideration window than e-commerce — renters
// often search, hesitate, talk to family/movers, then commit 2–4 weeks later.
// The resume card targets a city/hood page, never a specific listing, so
// stale entries always point to live inventory. Safe to keep wide.
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export type LastSearchPayload = {
  pillar: PillarPage
  city: SearchPageCity
  hood?: MexicoCityHoods
  /** Unix epoch ms when the entry was written. */
  timestamp: number
}

type DisplayPayload = LastSearchPayload & {
  pillarLabel: string
  cityName: string
  hoodName?: string
  /** Resolved URL to navigate back to. */
  href: string
}

const VALID_PILLARS = new Set<string>(Object.values(PillarPage))
const VALID_CITIES = new Set<string>(Object.values(SearchPageCity))
const VALID_HOODS = new Set<string>(Object.values(MexicoCityHoods))

// One-time availability probes. Some browsers expose `localStorage` /
// `sessionStorage` but throw on every method (Safari Private Browsing pre-iOS
// 11, sandboxed iframes, enterprise blockers, third-party cookies disabled in
// embedded contexts). Probing once at first use is cheaper than wrapping
// every call in try/catch — and lets the rest of the file early-return
// cleanly when storage is unusable.
let localStorageOk: boolean | null = null
let sessionStorageOk: boolean | null = null
const PROBE_KEY = '__spotme_storage_probe__'

function isLocalStorageAvailable(): boolean {
  if (localStorageOk !== null) return localStorageOk
  if (typeof window === 'undefined') {
    localStorageOk = false
    return false
  }
  try {
    window.localStorage.setItem(PROBE_KEY, '1')
    window.localStorage.removeItem(PROBE_KEY)
    localStorageOk = true
  } catch {
    localStorageOk = false
  }
  return localStorageOk
}

function isSessionStorageAvailable(): boolean {
  if (sessionStorageOk !== null) return sessionStorageOk
  if (typeof window === 'undefined') {
    sessionStorageOk = false
    return false
  }
  try {
    window.sessionStorage.setItem(PROBE_KEY, '1')
    window.sessionStorage.removeItem(PROBE_KEY)
    sessionStorageOk = true
  } catch {
    sessionStorageOk = false
  }
  return sessionStorageOk
}

function safeReadLocalStorage(key: string): string | null {
  if (!isLocalStorageAvailable()) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Writes a JSON-serializable value. Stringification happens inside the
 *  try block so any cycle / unsupported value (Symbol, BigInt, undefined)
 *  silently no-ops instead of throwing. */
function safeWriteLocalStorageJSON(key: string, value: unknown): void {
  if (!isLocalStorageAvailable()) return
  try {
    const serialized = JSON.stringify(value)
    if (typeof serialized !== 'string') return
    window.localStorage.setItem(key, serialized)
  } catch {
    // Cycle / quota / blocked — silently no-op.
  }
}

function safeRemoveLocalStorage(key: string): void {
  if (!isLocalStorageAvailable()) return
  try {
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

function safeReadSessionStorage(key: string): string | null {
  if (!isSessionStorageAvailable()) return null
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeWriteSessionStorage(key: string, value: string): void {
  if (!isSessionStorageAvailable()) return
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

function parsePayload(raw: string | null): LastSearchPayload | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<LastSearchPayload>
    if (
      typeof parsed.pillar !== 'string' ||
      typeof parsed.city !== 'string' ||
      typeof parsed.timestamp !== 'number'
    ) {
      return null
    }
    if (!VALID_PILLARS.has(parsed.pillar) || !VALID_CITIES.has(parsed.city)) {
      return null
    }
    if (parsed.hood !== undefined && !VALID_HOODS.has(parsed.hood)) {
      return null
    }
    if (Date.now() - parsed.timestamp > TTL_MS) {
      return null
    }
    return parsed as LastSearchPayload
  } catch {
    return null
  }
}

/**
 * Keeps the pre-paint layout reservation honest.
 *
 * `RESUME_RESERVATION_SCRIPT` in src/screens/Landing/Hero.tsx stamps
 * `data-resume-search="1"` on <html> before first paint from a cheap read of
 * raw storage, so `.resume-card-slot` can reserve the hero's resume-card
 * height and the card's mount costs zero layout shift. That guess skips the
 * enum validation this module does, so re-sync it here once the real payload
 * is resolved — otherwise an entry with a retired pillar/city slug would
 * reserve space for a card that never renders, leaving an empty gap in the
 * hero. Also clears it on dismiss so the slot collapses with the card.
 */
function syncResumeReservation(hasCard: boolean): void {
  if (typeof document === 'undefined') return
  if (hasCard) {
    document.documentElement.setAttribute('data-resume-search', '1')
    return
  }
  document.documentElement.removeAttribute('data-resume-search')
}

/** Reads storage and resolves the display-ready payload, or null when there
 *  is nothing to resume (dismissed, absent, malformed, expired, or pointing
 *  at a slug that no longer maps to a live pillar/city). */
function resolveDisplayPayload(): DisplayPayload | null {
  if (safeReadSessionStorage(DISMISS_KEY) === '1') return null

  const parsed = parsePayload(safeReadLocalStorage(STORAGE_KEY))
  if (!parsed) return null

  const pillarLabel = PILLAR_PAGE_MAP[parsed.pillar]?.label ?? ''
  const cityName = SEARCH_PAGE_CITY_MAP[parsed.city]?.cityName ?? ''
  if (!pillarLabel || !cityName) return null

  const hoodName = parsed.hood ? HOOD_MAP[parsed.hood]?.hoodName : undefined
  const href = parsed.hood
    ? `/${parsed.pillar}/${parsed.city}/${parsed.hood}`
    : `/${parsed.pillar}/${parsed.city}`

  return { ...parsed, pillarLabel, cityName, hoodName, href }
}

/**
 * Persists the renter's most recent pillar + city + (optional) hood into
 * localStorage so the home page can offer a one-tap resume on return.
 *
 * Failsafe: read/write are wrapped in try/catch (Safari ITP, quota, private
 * mode all silently no-op). Stale entries (older than `TTL_MS`) are treated
 * as missing. Session-level dismissal is honored — once the user clicks
 * Dismiss, the resume card stays hidden until they open a new tab/window.
 */
const useLastSearch = (): {
  /** Display-ready payload, null when nothing to resume or user dismissed. */
  lastSearch: DisplayPayload | null
  /** Persist a new entry. Call from pillar-pill click handlers. */
  saveLastSearch: (payload: Omit<LastSearchPayload, 'timestamp'>) => void
  /** Hide the resume card for the rest of this session. */
  dismissLastSearch: () => void
} => {
  const [payload, setPayload] = useState<DisplayPayload | null>(null)

  useEffect(() => {
    const resolved = resolveDisplayPayload()
    setPayload(resolved)
    syncResumeReservation(!!resolved)
  }, [])

  const saveLastSearch = useCallback(
    (next: Omit<LastSearchPayload, 'timestamp'>): void => {
      const full: LastSearchPayload = { ...next, timestamp: Date.now() }
      safeWriteLocalStorageJSON(STORAGE_KEY, full)
    },
    []
  )

  const dismissLastSearch = useCallback((): void => {
    safeWriteSessionStorage(DISMISS_KEY, '1')
    safeRemoveLocalStorage(STORAGE_KEY)
    setPayload(null)
    syncResumeReservation(false)
  }, [])

  return { lastSearch: payload, saveLastSearch, dismissLastSearch }
}

/**
 * Page-level write hook. Drop into a page component to record the visit as
 * the renter's "last search". Fires once per render cycle when the
 * (pillar, city, hood) tuple changes — idempotent across rerenders, safe to
 * call from SSR'd pages (the underlying write is window-guarded).
 *
 * Use on:
 *  - city / hood pages (search completion — visitor narrowed to a location)
 *  - listing detail page (visit completion — visitor opened a specific spot)
 *
 * Skips the write when `city` is missing, so empty search states (visitor
 * landed on `/[pillar]` with no geo yet) don't pollute the resume card with
 * a partial entry.
 */
export const useTrackLastSearch = (params: {
  pillar: PillarPage | null | undefined
  city: SearchPageCity | null | undefined
  hood?: MexicoCityHoods | null | undefined
}): void => {
  const { pillar, city, hood } = params
  useEffect(() => {
    if (!pillar || !city) return
    const payload: LastSearchPayload = {
      pillar,
      city,
      ...(hood ? { hood } : {}),
      timestamp: Date.now()
    }
    safeWriteLocalStorageJSON(STORAGE_KEY, payload)
  }, [pillar, city, hood])
}

export default useLastSearch
